import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import ResponseDrillTable, { toDrillRows } from './ResponseDrillTable';

// Which count an answer is added to. The drill-down reads the same rule, so an
// answer stored as "true" is found under the Yes it was counted under.
export const normalizeBoolean = (value) => {
  const lower = String(value).toLowerCase();
  if (['yes', 'y', 'true', '1'].includes(lower) || value === true || value === 1) return 'Yes';
  if (['no', 'n', 'false', '0'].includes(lower) || value === false || value === 0) return 'No';
  return 'Other';
};

const BooleanCanvas = ({ values = [], view = 'cards', chartRef, rows = [], onOpenSession }) => {
  // Which answer the author clicked. A boolean question draws counts as cards
  // rather than bars, so the card is what there is to click; the pie view gets
  // the same behaviour on its slices.
  const [drillAnswer, setDrillAnswer] = useState(null);
  const canDrill = rows.length > 0;

  if (values.length === 0) {
    return (
      <div style={{
        fontFamily: 'var(--font-family-primary)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: '#5F6368',
        fontSize: '14px'
      }}>
        No responses yet.
      </div>
    );
  }

  const normalized = values.map(normalizeBoolean);
  const counts = normalized.reduce((acc, val) => {
    acc[val] = (acc[val] || 0) + 1;
    return acc;
  }, {});

  const total = values.length;
  const yesCount = counts.Yes || 0;
  const noCount = counts.No || 0;
  const otherCount = counts.Other || 0;
  const yesPct = total ? Math.round((yesCount / total) * 100) : 0;
  const noPct = total ? Math.round((noCount / total) * 100) : 0;
  const otherPct = total ? Math.round((otherCount / total) * 100) : 0;

  const drillRows = drillAnswer == null
    ? []
    : toDrillRows(rows.filter(row => normalizeBoolean(row.value) === drillAnswer));

  // A second click on the answer already open closes it.
  const handleAnswerClick = (answer) => {
    if (!canDrill) return;
    setDrillAnswer(current => (current === answer ? null : answer));
  };

  const drillTable = drillAnswer != null && (
    <ResponseDrillTable
      label={drillAnswer}
      rows={drillRows}
      onClose={() => setDrillAnswer(null)}
      onOpenSession={onOpenSession}
    />
  );

  const drillHint = canDrill && (
    <div style={{ fontSize: 12, color: '#5F6368', marginTop: 8 }}>
      Click {view === 'pie' ? 'a slice' : 'a count'} to see its answers.
    </div>
  );

  const cardStyle = {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E8EAED',
    borderRadius: '8px',
    padding: '16px',
    flex: '1 1 120px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: 'var(--font-family-primary)'
  };

  const countStyle = {
    fontSize: '28px',
    fontWeight: '600',
    color: '#202124',
    margin: '0 0 4px 0'
  };

  const labelStyle = {
    fontSize: '14px',
    color: '#202124',
    margin: '0 0 4px 0'
  };

  const pctStyle = {
    fontSize: '12px',
    color: '#5F6368'
  };

  if (view === 'pie') {
    const data = [
      { name: 'Yes', value: yesCount },
      { name: 'No', value: noCount },
      ...(otherCount > 0 ? [{ name: 'Other', value: otherCount }] : [])
    ];
    const COLORS = ['#10b981', '#ef4444', '#9AA0A6'];

    // The drill-down sits outside chartRef: it is a way into the answers, not
    // part of the picture the author exports as a PNG.
    return (
      <div style={{ fontFamily: 'var(--font-family-primary)', width: '100%' }}>
        <div ref={chartRef}>
        <ResponsiveContainer width="100%" height={420}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine
              outerRadius={155}
              fill="#8884d8"
              dataKey="value"
              label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
              onClick={(entry) => handleAnswerClick(entry?.name ?? entry?.payload?.name ?? null)}
              cursor={canDrill ? 'pointer' : undefined}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
        <div aria-label="Boolean response legend" role="list" style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', fontSize: 12, color: '#3C4043' }}>
          {data.map((entry, index) => (
            <span key={entry.name} role="listitem" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: COLORS[index % COLORS.length] }} />
              {entry.name}: {entry.value} ({Math.round((entry.value / total) * 100)}%)
            </span>
          ))}
        </div>
        </div>
        {drillHint}
        {drillTable}
      </div>
    );
  }

  // A count is what this view draws instead of a bar, so it is what an author
  // clicks to reach the answers behind it. It stays a plain card when there are
  // no rows to reach, rather than offering a button that does nothing.
  const renderCard = (answer, count, pct) => {
    const content = <>
      <div style={countStyle}>{count}</div>
      <div style={labelStyle}>{answer}</div>
      <div style={pctStyle}>{pct}%</div>
    </>;
    if (!canDrill) return <div key={answer} style={cardStyle}>{content}</div>;
    const open = drillAnswer === answer;
    return (
      <button
        key={answer}
        type="button"
        onClick={() => handleAnswerClick(answer)}
        title={`Show the ${answer} answers`}
        aria-pressed={open}
        style={{ ...cardStyle, cursor: 'pointer', textAlign: 'center', borderColor: open ? '#5B9FFF' : '#E8EAED' }}
      >
        {content}
      </button>
    );
  };

  return (
    <div style={{ fontFamily: 'var(--font-family-primary)' }}>
      <div ref={chartRef}>
      <div style={{
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        marginBottom: '16px'
      }}>
        {renderCard('Yes', yesCount, yesPct)}
        {renderCard('No', noCount, noPct)}
        {otherCount > 0 && renderCard('Other', otherCount, otherPct)}
      </div>
      <div style={{
        height: '14px',
        backgroundColor: '#E8EAED',
        borderRadius: '7px',
        overflow: 'hidden',
        display: 'flex',
        position: 'relative'
      }}>
        {yesCount > 0 && (
          <div style={{
            width: `${(yesCount / total) * 100}%`,
            backgroundColor: '#10b981',
            height: '100%'
          }} />
        )}
        {noCount > 0 && (
          <div style={{
            width: `${(noCount / total) * 100}%`,
            backgroundColor: '#ef4444',
            height: '100%'
          }} />
        )}
      </div>
      <div style={{
        display: 'flex',
        gap: '12px',
        marginTop: '8px',
        fontSize: '10px',
        color: '#5F6368'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: '10px',
            height: '10px',
            backgroundColor: '#10b981',
            borderRadius: '2px',
            marginRight: '4px'
          }} />
          Yes
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: '10px',
            height: '10px',
            backgroundColor: '#ef4444',
            borderRadius: '2px',
            marginRight: '4px'
          }} />
          No
        </div>
      </div>
      </div>
      {drillHint}
      {drillTable}
    </div>
  );
};

export default BooleanCanvas;
